import { Component, ElementRef, EventEmitter, Input, Output, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export type SecureMediaPlayerType = 'audio' | 'video';

@Component({
    selector: 'app-secure-media-player',
    templateUrl: './secure-media-player.component.html',
    styleUrl: './secure-media-player.component.scss',
    standalone: false
})
export class SecureMediaPlayerComponent {
  @Input() mediaUrl: SafeUrl | null = null;
  @Input() mediaType: SecureMediaPlayerType = 'video';
  /** Playback window, in seconds. */
  @Input() startTime?: number;
  @Input() endTime?: number;

  @Output() loaded = new EventEmitter<void>();
  @Output() failed = new EventEmitter<void>();

  @ViewChild('mediaPlayer', { static: false }) mediaPlayer: ElementRef<HTMLMediaElement>;

  duration = 0;
  progress = 0;
  currentTimeSec = 0;
  isMediaPlaying = false;
  isMuted = false;
  isMediaLoading = true;
  segmentPlaybackEnded = false;
  private ignoreNextSeek = false;

  constructor(private readonly sanitizer: DomSanitizer) {}

  // Angular 20 no longer treats audio[src] as a URL-sanitized property, so a SafeUrl is stringified; bind the plain URL.
  get audioSrc(): string | null {
    return this.mediaUrl ? this.sanitizer.sanitize(SecurityContext.URL, this.mediaUrl) : null;
  }
  get isVideo(): boolean {
    return this.mediaType === 'video';
  }

  onMediaMetadata(): void {
    const media = this.mediaPlayer?.nativeElement;
    this.duration = Number.isFinite(media?.duration) ? media.duration : 0;
    if (media && this.startTime != null && this.duration > 0) {
      this.ignoreNextSeek = true;
      media.currentTime = Math.max(0, Math.min(this.startTime, this.duration));
    }
    this.loaded.emit();
    this.startAutoplay();
  }

  onMediaReady(): void {
    this.isMediaLoading = false;
  }

  onMediaError(): void {
    this.isMediaLoading = false;
    this.failed.emit();
  }

  // Browsers/mobile block audible autoplay without a gesture; retry muted so playback always starts.
  private startAutoplay(): void {
    const media = this.mediaPlayer?.nativeElement;
    if (!media) {
      return;
    }
    media.play().catch(() => {
      media.muted = true;
      this.isMuted = true;
      media.play().catch(() => { this.isMediaPlaying = false; });
    });
  }

  toggleMute(): void {
    const media = this.mediaPlayer?.nativeElement;
    if (!media) {
      return;
    }
    media.muted = !media.muted;
    this.isMuted = media.muted;
    if (media.paused) {
      Promise.resolve(media.play()).catch(() => { this.isMediaPlaying = false; });
    }
  }

  onMediaTimeUpdate(): void {
    const media = this.mediaPlayer?.nativeElement;
    if (!media) {
      return;
    }

    this.currentTimeSec = media.currentTime;
    this.progress = this.duration > 0 ? (media.currentTime / this.duration) * 100 : 0;
    if (!this.segmentPlaybackEnded && this.endTime != null && media.currentTime >= this.endTime) {
      this.segmentPlaybackEnded = true;
      media.pause();
    }
  }

  toggleMediaPlay(): void {
    const media = this.mediaPlayer?.nativeElement;
    if (!media) {
      return;
    }

    if (media.paused) {
      if (this.segmentPlaybackEnded && this.endTime != null && media.currentTime >= this.endTime) {
        this.segmentPlaybackEnded = false;
        media.currentTime = this.startTime ?? 0;
      }
      // play() may reject (autoplay policy) or return undefined on old browsers; normalize and swallow.
      Promise.resolve(media.play()).catch(() => { this.isMediaPlaying = false; });
    } else {
      media.pause();
    }
  }

  onMediaSeeking(): void {
    if (this.ignoreNextSeek) {
      this.ignoreNextSeek = false;
      return;
    }
    this.segmentPlaybackEnded = true;
  }

  onSeekInput(event: Event): void {
    const media = this.mediaPlayer?.nativeElement;
    const value = Number((event.target as HTMLInputElement).value);
    if (!media || Number.isNaN(value)) {
      return;
    }
    this.segmentPlaybackEnded = true;
    media.currentTime = value;
    this.currentTimeSec = value;
    this.progress = this.duration > 0 ? (value / this.duration) * 100 : 0;
  }

  get segmentStartPercent(): number {
    return this.duration && this.startTime != null
      ? Math.max(0, Math.min(100, (this.startTime / this.duration) * 100))
      : 0;
  }

  get segmentWidthPercent(): number {
    return this.duration && this.startTime != null && this.endTime != null
      ? Math.max(0, Math.min(100 - this.segmentStartPercent, ((this.endTime - this.startTime) / this.duration) * 100))
      : 0;
  }

  get segmentEndPercent(): number {
    return Math.min(100, this.segmentStartPercent + this.segmentWidthPercent);
  }

  get segmentProgressPercent(): number {
    if (this.startTime == null || this.endTime == null || this.endTime <= this.startTime) {
      return 0;
    }

    return Math.max(0, Math.min(100, ((this.progress / 100 * this.duration - this.startTime) / (this.endTime - this.startTime)) * 100));
  }

  formatTime(seconds: number | undefined): string {
    if (seconds == null || !Number.isFinite(seconds)) {
      return '00:00';
    }

    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const secs = String(total % 60).padStart(2, '0');
    return hours > 0 ? `${hours}:${minutes}:${secs}` : `${minutes}:${secs}`;
  }
}
